import React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import jaLocale from '@fullcalendar/core/locales/ja';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

import { scheduleEventColors } from '../../utils/colors';
import { 
  createDateClickHandler,
  createEventClickHandler,
  createEventDropHandler
} from '../../utils/eventHandlers';
import {
  createViewChangeHandler,
  createEventContentRenderer,
  filterEvents
} from '../../utils/eventHelpers';

const CalendarMain = ({ isMobile, calendarState, calendarEvents, currentUser }) => {
  const handleDateClick = createDateClickHandler(calendarState);
  const handleEventClick = createEventClickHandler(calendarState);
  const handleEventDrop = createEventDropHandler(calendarEvents);
  const handleViewChange = createViewChangeHandler(calendarState);
  const renderEventContent = createEventContentRenderer(calendarState.currentView);

  const filteredEvents = filterEvents(
    calendarEvents.events,
    calendarState.selectedCategories,
    calendarState.selectedAreas
  );

  return (
    <>
      {/* スマートフォン用のハンバーガーメニューボタン */}
      {isMobile && (
        <IconButton
          edge="start"
          color="inherit"
          aria-label="menu"
          onClick={calendarState.toggleDrawer(true)}
          sx={{ position: 'absolute', top: 8, right: 16, color: '#ffffff' }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* メインエリア：カレンダー表示 */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          maxWidth: 1680,
          width: '100%',   // 幅を100%に設定
          mx: 'auto',      // 中央寄せ
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        inert={calendarState.open ? '' : undefined}
      >
        {/* カレンダー本体 */}
        <FullCalendar
          ref={calendarState.calendarRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView={sessionStorage.getItem('scheduleCalendarView') || (isMobile ? 'listWeek' : 'dayGridMonth')}
          initialDate={sessionStorage.getItem('calendarDate') || undefined}
          events={filteredEvents.map((event) => ({
            ...event,
            end: new Date(new Date(event.end).getTime() + 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0],
            backgroundColor: event.category === 'プライベート'
              ? scheduleEventColors.privateBackground
              : scheduleEventColors.background,
            borderColor: event.category === 'プライベート'
              ? scheduleEventColors.privateBorder
              : scheduleEventColors.border,
          }))}
          eventContent={renderEventContent}
          locale={jaLocale}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          editable={true}
          selectable={true}
          viewDidMount={handleViewChange}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
          }}
          datesSet={(info) => {
            calendarState.setCurrentView(info.view.type);
            sessionStorage.setItem('scheduleCalendarView', info.view.type);
            sessionStorage.setItem('calendarDate', info.view.currentStart.toISOString());
          }}
          dayHeaderContent={(args) => {
            if (isMobile && calendarState.currentView === 'dayGridWeek') {
              const date = new Date(args.date);
              return date.getDate();
            }
            if (isMobile && calendarState.currentView.startsWith('list')) {
              const date = new Date(args.date);
              const options = { weekday: 'short', day: 'numeric' };
              return date.toLocaleDateString('ja-JP', options);
            }
            return args.text;
          }}
        />
      </Box>
    </>
  );
};

export default CalendarMain;